import { useCallback, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { type WorkflowTab, WorkflowTabBar } from '@/components/common';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';

import { FreezeActionBar } from './components/FreezeActionBar';
import { FreezeSelector } from './components/FreezeSelector';
import { FreezeStatusBar } from './components/FreezeStatusBar';
import { FertilizationGrid } from './components/grids';
import {
  GradingTab,
  MetricsTab,
  QCTab,
  SetupTab,
  ValidationTab,
} from './components/tabs';
import {
  FreezeFormProvider,
  FreezeGridProvider,
  FreezeSelectionProvider,
  type FreezeTab,
  FreezeViewProvider,
  useFreezeGrid,
  useFreezeView,
} from './contexts';
import { useCellEdit } from './hooks/useCellEdit';
import { type DayFilter, useDayFilter } from './hooks/useDayFilter';
import { useFreezeData } from './hooks/useFreezeData';
import { useGridNav } from './hooks/useGridNav';
import { MOBILE_BREAKPOINT } from './utils/constants';

const FREEZE_TABS: WorkflowTab<FreezeTab>[] = [
  { key: 'setup', label: 'Setup', icon: 'pi pi-cog' },
  { key: 'qc', label: 'QC', icon: 'pi pi-check-square' },
  { key: 'grading', label: 'Grading', icon: 'pi pi-th-large' },
  { key: 'validation', label: 'Validation', icon: 'pi pi-verified' },
  { key: 'metrics', label: 'Metrics', icon: 'pi pi-chart-bar' },
];

const FreezeV2PageContent = () => {
  const { activeTab, setTab } = useFreezeView();
  const { gridApi } = useFreezeGrid();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const [params] = useSearchParams();
  const fertilizationDate = params.get('date') || '';
  const fertilizationSiteId = params.get('sId');
  const hasContext = !!fertilizationDate && !!fertilizationSiteId;

  const [quickSearchValue, setQuickSearchValue] = useState('');
  const [tabPanelOpen, setTabPanelOpen] = useState(true);

  const {
    fertilizations,
    embryosByFert,
    isLoadingFerts,
    isLoadingEmbryos,
    refetchAll,
  } = useFreezeData({
    fertilizationDate,
    fertilizationSiteId,
    searchQuery: quickSearchValue,
  });

  const { handleFertCellValueChanged } = useCellEdit();

  const { handleCellKeyDown } = useGridNav();

  const { activeDayFilter, applyDayFilter } = useDayFilter({
    gridApi,
    embryosByFert,
  });

  const handleRefresh = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  const handleSearchChange = useCallback((value: string) => {
    setQuickSearchValue(value);
  }, []);

  return (
    <div className="flex h-full flex-col gap-1.5 p-2 md:p-2.5">
      <div
        className={`flex gap-2 ${isMobile ? 'flex-col' : 'flex-row flex-wrap items-center'}`}
      >
        <FreezeSelector
          quickSearchValue={quickSearchValue}
          setQuickSearchValue={handleSearchChange}
        />
        {hasContext && (
          <div className={isMobile ? '' : 'ml-auto'}>
            <FreezeActionBar
              onRefresh={handleRefresh}
              isLoading={isLoadingFerts || isLoadingEmbryos}
              fertilizations={fertilizations}
              embryosByFert={embryosByFert}
              activeDayFilter={activeDayFilter}
              onDayFilterChange={applyDayFilter}
            />
          </div>
        )}
      </div>

      {!hasContext ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
          <i className="pi pi-calendar text-3xl text-gray-400" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Select a fertilization date and site to begin
          </p>
          <p className="text-muted-foreground text-xs">
            Choose a date and site above to load fertilizations and embryos for
            freezing.
          </p>
        </div>
      ) : (
        <>
          <div>
            <div
              className={`flex gap-2 ${isMobile ? 'flex-col' : 'flex-row items-center justify-between'}`}
            >
              <WorkflowTabBar<FreezeTab>
                tabs={FREEZE_TABS}
                activeTab={activeTab}
                onTabChange={(tab) => {
                  if (tab === activeTab) {
                    setTabPanelOpen((prev) => !prev);
                  } else {
                    setTab(tab);
                    setTabPanelOpen(true);
                  }
                }}
              />
              <FreezeStatusBar />
            </div>
            {tabPanelOpen && (
              <div className="bg-white p-2 dark:bg-gray-900">
                {activeTab === 'setup' && <SetupTab onUpdated={refetchAll} />}
                {activeTab === 'qc' && <QCTab />}
                {activeTab === 'grading' && <GradingTab />}
                {activeTab === 'validation' && (
                  <ValidationTab
                    fertilizations={fertilizations}
                    embryosByFert={embryosByFert}
                  />
                )}
                {activeTab === 'metrics' && (
                  <MetricsTab
                    fertilizations={fertilizations}
                    embryosByFert={embryosByFert}
                    onRefresh={refetchAll}
                    isRefreshing={isLoadingFerts || isLoadingEmbryos}
                  />
                )}
              </div>
            )}
          </div>

          <FertilizationGrid
            fertilizations={fertilizations}
            embryosByFert={embryosByFert}
            isLoading={isLoadingFerts}
            fertilizationSiteId={fertilizationSiteId}
            onFertCellValueChanged={handleFertCellValueChanged}
            onCellKeyDown={handleCellKeyDown}
            // discards/adds change intEmbs/embs on the fertilization row too
            onRefreshEmbryos={refetchAll}
          />
        </>
      )}
    </div>
  );
};

export const FreezeV2Page = () => {
  return (
    <FreezeViewProvider>
      <FreezeSelectionProvider>
        <FreezeFormProvider>
          <FreezeGridProvider>
            <FreezeV2PageContent />
          </FreezeGridProvider>
        </FreezeFormProvider>
      </FreezeSelectionProvider>
    </FreezeViewProvider>
  );
};
